import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

import type { WorkHourFormat } from './board-config.api';

/** Global (account-level) application settings. */
export interface AppSettings {
  /** Account-level default: how work hours are displayed (per_day or total) */
  workHourFormat: WorkHourFormat;
  /** Account-level default: whether to divide hours equally among assignees */
  perPersonDistribution: boolean;
  /** Whether all monday.com users can access the app (vs. specific users only) */
  allUsersAccessEnabled: boolean;
  updatedAt: string;
}

/** Per-user settings. */
export interface UserSettings {
  /** Default approver for leave requests (monday.com user ID as string) */
  defaultLeaveApprover: string | null;
  updatedAt: string;
}

/** User access record from user management API. */
export interface UserAccess {
  mondayUserId: string;
  userName: string;
  email: string;
  isAdmin: boolean;
  isRestricted: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── App Settings API ─────────────────────────────────────────────────────────

/** Get global application settings. */
export async function getAppSettings(): Promise<AppSettings> {
  const { data } = await apiClient.get<AppSettings>('/settings/app');
  return data;
}

/** Update global application settings. */
export async function updateAppSettings(
  payload: Partial<Omit<AppSettings, 'updatedAt'>>,
): Promise<AppSettings> {
  const { data } = await apiClient.put<AppSettings>('/settings/app', payload);
  return data;
}

// ─── User Settings API ────────────────────────────────────────────────────────

/** Get per-user settings for the current user. */
export async function getUserSettings(): Promise<UserSettings> {
  const { data } = await apiClient.get<UserSettings>('/settings/user');
  return data;
}

/** Update per-user settings for the current user. */
export async function updateUserSettings(
  payload: Partial<Omit<UserSettings, 'updatedAt'>>,
): Promise<UserSettings> {
  const { data } = await apiClient.put<UserSettings>(
    '/settings/user',
    payload,
  );
  return data;
}

// ─── User Management API ──────────────────────────────────────────────────────

/** Get the current user's admin/access status. */
export async function getCurrentUser(): Promise<{ mondayUserId: string; isAdmin: boolean; isRestricted: boolean }> {
  const { data } = await apiClient.get<{ mondayUserId: string; isAdmin: boolean; isRestricted: boolean }>('/users/me');
  return data;
}

/** List all users with access to the resource planning plugin. */
export async function getUsers(): Promise<UserAccess[]> {
  const { data } = await apiClient.get<UserAccess[]>('/users');
  return data;
}

/** Grant a monday.com user access to the plugin. */
export async function allowUser(mondayUserId: string): Promise<UserAccess> {
  const { data } = await apiClient.post<UserAccess>('/users/allow', {
    mondayUserId,
  });
  return data;
}

/** Remove a user's access to the plugin. */
export async function removeUserAccess(mondayUserId: string): Promise<void> {
  await apiClient.delete(`/users/allow/${mondayUserId}`);
}

/** Grant admin privileges to a user. */
export async function grantAdmin(mondayUserId: string): Promise<UserAccess> {
  const { data } = await apiClient.post<UserAccess>('/users/admin', {
    mondayUserId,
  });
  return data;
}

/** Revoke admin privileges from a user. */
export async function revokeAdmin(mondayUserId: string): Promise<UserAccess> {
  const { data } = await apiClient.delete<UserAccess>(
    `/users/admin/${mondayUserId}`,
  );
  return data;
}

/** Restrict a user (limited access). */
export async function restrictUser(mondayUserId: string): Promise<UserAccess> {
  const { data } = await apiClient.post<UserAccess>('/users/restrict', {
    mondayUserId,
  });
  return data;
}

/** Remove restriction from a user. */
export async function unrestrictUser(
  mondayUserId: string,
): Promise<UserAccess> {
  const { data } = await apiClient.delete<UserAccess>(
    `/users/restrict/${mondayUserId}`,
  );
  return data;
}

// ─── My Approvees API ────────────────────────────────────────────────────────

/** Get the list of users the current user is the designated approver for. */
export async function getMyApprovees(): Promise<{ mondayUserId: string }[]> {
  const { data } = await apiClient.get<{ mondayUserId: string }[]>(
    '/settings/my-approvees',
  );
  return data;
}

// ─── Leave Approver Admin API ────────────────────────────────────────────────

/** Mapping of a user to their admin-assigned leave approver. */
export interface LeaveApproverMapping {
  mondayUserId: string;
  defaultLeaveApprover: string | null;
  updatedAt: string;
}

/** Get all leave approver assignments for the account (admin only). */
export async function getLeaveApprovers(): Promise<LeaveApproverMapping[]> {
  const { data } = await apiClient.get<LeaveApproverMapping[]>(
    '/settings/leave-approvers',
  );
  return data;
}

/** Set the leave approver for a specific user (admin only). */
export async function setLeaveApprover(
  mondayUserId: string,
  approverId: string | null,
): Promise<LeaveApproverMapping> {
  const { data } = await apiClient.put<LeaveApproverMapping>(
    `/settings/leave-approvers/${mondayUserId}`,
    { approverId },
  );
  return data;
}

/** Bulk-assign a leave approver to multiple users (admin only). */
export async function bulkSetLeaveApprovers(
  userIds: string[],
  approverId: string | null,
): Promise<LeaveApproverMapping[]> {
  const { data } = await apiClient.put<LeaveApproverMapping[]>(
    '/settings/leave-approvers/bulk',
    { userIds, approverId },
  );
  return data;
}

